import React from "react";

/**
 * Icon - inline SVG icon set used across detail modals.
 *
 * Icons inherit the surrounding text colour (`stroke="currentColor"`) and are
 * sized in `em` by default so they line up with the text they sit next to.
 * Use these instead of emoji: emoji render at a different weight per platform
 * and can't be recoloured.
 *
 * Props:
 * - name: key of PATHS below
 * - size: any CSS length (default "1em")
 * - className: forwarded to the <svg>
 */
const PATHS = {
  file: (
    <>
      <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
      <path d="M13 2v7h7" />
    </>
  ),
  paperclip: (
    <path d="M21.4 11.05 12.25 20.2a5.5 5.5 0 0 1-7.78-7.78l9.19-9.19a3.67 3.67 0 0 1 5.18 5.18l-9.2 9.2a1.83 1.83 0 0 1-2.59-2.6l8.5-8.49" />
  ),
  trash: (
    <>
      <path d="M3 6h18" />
      <path d="M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
      <path d="M10 11v6M14 11v6" />
    </>
  ),
  close: <path d="M18 6 6 18M6 6l12 12" />,
  plus: <path d="M12 5v14M5 12h14" />,
  check: <path d="m20 6-11 11-5-5" />,
  arrowRight: (
    <>
      <path d="M5 12h14" />
      <path d="m12 5 7 7-7 7" />
    </>
  ),
  calendar: (
    <>
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4M8 2v4M3 10h18" />
    </>
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
    </>
  ),
  upload: (
    <>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <path d="M17 8l-5-5-5 5" />
      <path d="M12 3v12" />
    </>
  ),
  send: <path d="M22 2 11 13M22 2l-7 20-4-9-9-4z" />,

  /* Property-row glyphs — these carry the structure of the detail list, so the
     eye has something to scan down instead of a column of grey words. */
  status: (
    <>
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="3.5" fill="currentColor" stroke="none" />
    </>
  ),
  flag: (
    <>
      <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V4s-1 1-4 1-5-2-8-2-4 1-4 1z" />
      <path d="M4 22v-7" />
    </>
  ),
  tag: (
    <>
      <path d="M20.6 13.4 12 22l-9-9V3h10l7.6 7.6a2 2 0 0 1 0 2.8z" />
      <circle cx="7.5" cy="7.5" r="1.2" fill="currentColor" stroke="none" />
    </>
  ),
  mapPin: (
    <>
      <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z" />
      <circle cx="12" cy="10" r="3" />
    </>
  ),
  building: (
    <>
      <path d="M4 22V4a1 1 0 0 1 1-1h9a1 1 0 0 1 1 1v18" />
      <path d="M15 9h4a1 1 0 0 1 1 1v12" />
      <path d="M2 22h20" />
      <path d="M8 7h3M8 11h3M8 15h3" />
    </>
  ),
  text: <path d="M4 6h16M4 12h16M4 18h11" />,
  user: (
    <>
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </>
  ),
  mail: (
    <>
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="m2 7 10 6 10-6" />
    </>
  ),
  phone: (
    <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 1.9.7 2.8a2 2 0 0 1-.5 2.1L8.1 9.9a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.8.7a2 2 0 0 1 1.7 2z" />
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </>
  ),
  wallet: (
    <>
      <path d="M20 8V6a2 2 0 0 0-2-2H5a2 2 0 0 0 0 4h15a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6" />
      <circle cx="17" cy="14" r="1.2" fill="currentColor" stroke="none" />
    </>
  ),

  /* Navigation glyphs (sidebar + topbar) — each has one soft duotone fill so
     the set reads as a family. */
  dashboard: (
    <>
      <rect x="3" y="3" width="8" height="9" rx="2" fill="currentColor" fillOpacity="0.2" />
      <rect x="13" y="3" width="8" height="5" rx="2" />
      <rect x="13" y="12" width="8" height="9" rx="2" />
      <rect x="3" y="16" width="8" height="5" rx="2" />
    </>
  ),
  leads: (
    <>
      <path d="M3 4h18l-7 8.5V19l-4 2v-8.5z" fill="currentColor" fillOpacity="0.2" />
      <path d="M7 8h10" />
    </>
  ),
  projects: (
    <>
      <path d="m12 3 9 4.5-9 4.5-9-4.5z" fill="currentColor" fillOpacity="0.2" />
      <path d="m3 12 9 4.5 9-4.5" />
      <path d="m3 16.5 9 4.5 9-4.5" />
    </>
  ),
  tasks: (
    <>
      <rect x="3" y="3" width="18" height="18" rx="3" fill="currentColor" fillOpacity="0.2" />
      <path d="m8 12 3 3 5-6" />
    </>
  ),
  schedule: (
    <>
      <rect x="3" y="4" width="18" height="17" rx="2.5" />
      <path d="M16 2v4M8 2v4M3 10h18" />
      <rect x="7" y="13.5" width="6" height="4" rx="1" fill="currentColor" fillOpacity="0.35" stroke="none" />
    </>
  ),
  timesheets: (
    <>
      <circle cx="12" cy="13.5" r="8" fill="currentColor" fillOpacity="0.2" />
      <path d="M12 9.5v4l2.5 2M10 2h4M12 2v3.5M19 6l1.5-1.5" />
    </>
  ),
  leave: (
    <>
      <path d="M12 3a9 9 0 0 1 9 9H3a9 9 0 0 1 9-9z" fill="currentColor" fillOpacity="0.2" />
      <path d="M12 3c-2.2 2.2-3 5.5-3 9M12 3c2.2 2.2 3 5.5 3 9M12 12v6.5a2.5 2.5 0 0 0 5 0" />
    </>
  ),
  people: (
    <>
      <circle cx="9" cy="7.5" r="4" fill="currentColor" fillOpacity="0.2" />
      <path d="M2 21v-1a6 6 0 0 1 6-6h2a6 6 0 0 1 6 6v1" />
      <path d="M16 3.6a4 4 0 0 1 0 7.8M22 21v-1a6 6 0 0 0-3.5-5.5" />
    </>
  ),
  reports: (
    <>
      <rect x="3" y="3" width="18" height="18" rx="2.5" fill="currentColor" fillOpacity="0.2" />
      <path d="M7.5 16.5v-4M12 16.5v-9M16.5 16.5v-6" />
    </>
  ),
  directory: (
    <>
      <rect x="5" y="2.5" width="15" height="19" rx="2.5" />
      <circle cx="12.5" cy="10" r="3" fill="currentColor" fillOpacity="0.2" />
      <path d="M8.5 17.5a4 4 0 0 1 8 0M3 7h3.5M3 12h3.5M3 17h3.5" />
    </>
  ),
  settings: (
    <>
      <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" fill="currentColor" fillOpacity="0.2" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  logout: (
    <>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <path d="m16 17 5-5-5-5M21 12H9" />
    </>
  ),
  bell: (
    <>
      <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" fill="currentColor" fillOpacity="0.2" />
      <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
    </>
  ),
};

export default function Icon({ name, size = "1em", className, ...rest }) {
  const path = PATHS[name];
  if (!path) return null;

  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      style={{ flexShrink: 0, display: "block" }}
      {...rest}
    >
      {path}
    </svg>
  );
}
